// Translations for the application (English only)
export type Language = "en";

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.international": "International",
    "nav.faqs": "FAQs",
    "nav.blogs": "Blogs",
    "nav.contact": "Contact Us",

    // Hero Section
    "hero.save": "Save 60% On Your",
    "hero.space": "Space Today.",
    "hero.feature1": "Walking distance to terminal",
    "hero.feature2": "Monitored Car Parks",
    "hero.feature3": "24/7 Live Support",

    // Booking Form
    "booking.entryDate": "Entry Date",
    "booking.entryTime": "Entry Time",
    "booking.exitDate": "Exit Date",
    "booking.exitTime": "Exit Time",
    "booking.promoCode": "Promo Code",
    "booking.enterPromo": "Enter promo code",
    "booking.selectEntryDate": "Select entry date",
    "booking.selectExitDate": "Select exit date",
    "booking.termsText": "By clicking Find Parking, you agree to our",
    "booking.termsLink": "terms & conditions",
    "booking.findParking": "Find Parking",
    "booking.dropoff": "Drop-off",
    "booking.pickup": "Pick-up",
    "booking.search": "Search",
    "booking.selectDate": "Select date",
    "booking.selectTime": "Select time",

    // Partners
    "partners.title": "Our Trusted Partners",

    // Network
    "network.view": "View our network →",

    // Testimonials
    "testimonials.title": "What Our Customers Say",
    "testimonials.viewMore": "View More",
    "testimonials.frequentFlyer": "Frequent Flyer",
    "testimonials.businessTraveller": "Business Traveller",
    "testimonials.businessOwner": "Business Owner",
    "testimonials.holidayTraveller": "Holiday Traveller",
    "testimonials.regularCustomer": "Regular Customer",
    "testimonials.firstTimeUser": "First Time User",
    "testimonials.familyVacation": "Family Vacation",
    "testimonials.weekendGetaway": "Weekend Getaway",
    "testimonials.businessTrip": "Business Trip",

    // How It Works
    "how.step1.title": "Select Dates",
    "how.step1.desc": "Start In A Blink Of An Eye",
    "how.step2.title": "Choose Parking Product",
    "how.step2.desc": "Choose Parking Product That Suits Your Need & Budget",
    "how.step3.title": "Easy to Book",
    "how.step3.desc": "Just Confirm Details & Pay With An Easy & Fast Booking Process",

    // Why Choose Us
    "why.title": "Why Choose Us?",
    "why.subtitle":
      "Avoid the stress of searching for a space on the day you travel. By booking your airport parking in advance, you secure the best available rate and guarantee your space — giving you a smooth, worry-free start to your journey.",
    "why.feature1": "Guaranteed Parking Availability",
    "why.feature2": "24/7 Customer Support",
    "why.feature3": "Free Shuttle Bus 24/7",
    "why.feature4": "Luggage Assistance",
    "why.feature5": "Patrolled & Secure Car Parks",
    "why.feature6": "Professional, Friendly Staff",
    "why.feature7": "Choice of Service Options",
    "why.feature8": "Vehicle Collected & Returned to You",
    "why.stat1": "Years of Service",
    "why.stat2": "Happy Customers",
    "why.stat3": "Cars Parked Daily",
    "why.stat4": "Industry Awards",
    "why.cta": "Pre-book today for secure, reliable, and affordable airport parking designed to make your travel experience easier from the moment you arrive.",

    // Services
    "services.title": "Our Services",
    "services.subtitle": "Global Operations & Services",
    "services.meetGreet.title": "Meet & Greet",
    "services.meetGreet.desc":
      "The fastest and easiest way to park at the airport is to drive straight to the terminal entrance, hand over your car to the driver, grab your luggage, and you're off. When you return, just give the driver a call, and they'll bring your car to meet you at the terminal entrance.",
    "services.parkRide.title": "Park & Ride",
    "services.parkRide.desc":
      "The most budget-friendly parking option, typically involving a 5 to 10 minute transfer via shuttle. Most Park & Ride facilities are highly secure and provide personalized customer service. Shuttles usually operate frequently and are available on demand for your convenience.",

    // About Section (Home Page)
    "about.title": "About Go Airport Parking",
    "about.welcome": "Welcome to Go Airport Parking",
    "about.desc1":
      ", an independent and privately operated provider of secure, reliable airport parking across the UK. For more than a decade, we've built a strong reputation for delivering affordable parking options that make travel simpler and less stressful. Our goal is straightforward: to give every customer a smooth start to their journey with trusted service, competitive prices, and an easy online booking experience.",
    "about.desc2":
      "We're now proud to bring our expertise to Leeds Airport parking, offering dependable solutions designed around convenience and peace of mind. Whether you're heading away for business or a well-earned holiday, we provide secure parking options close to the terminal, backed by professional local operators.",
    "about.desc3":
      "At Go Airport Parking, we focus on value, security, and service. From streamlined booking to reliable transfers and well-managed facilities, our team is committed to delivering a seamless experience every time you choose parking at Leeds Bradford Airport.",
    "about.desc4": "Book with confidence and enjoy affordable, stress-free parking from a name travelers trust.",
    "about.badge1": "Monitored Parking",
    "about.badge2": "Friendly & Trained Staff",
    "about.badge3": "Free Shuttle Transfers",
    "about.badge4": "Best Price Guarantee",
    "about.brand.title": "Go Airport Parking — A Trusted UK Brand",
    "about.brand.p1": "For over a decade,",
    "about.brand.p1b":
      "Go Airport Parking has been a trusted name in airport parking services across the UK. Our mission is simple: to make your journey smoother by offering a wide range of secure, reliable, and affordable airport parking options. With our easy-to-use online booking platform and a network of professional parking operators, we ensure outstanding value and peace of mind for every traveler.",
    "about.brand.p2":
      "Discover our trusted airport parking solutions at the United Kingdom's busiest airports, including Heathrow, Gatwick, Manchester, and more. We've built our reputation on providing safe, convenient, and cost-effective parking, so you can travel with confidence knowing your car is in good hands. Whether you need short stay, long stay, or meet-and-greet airport parking,",
    "about.brand.p2b": "is here to make your journey stress-free.",

    // Home Page FAQ Section
    "faq.title": "Frequently Asked Questions",
    "faq.subtitle": "Have questions about our services? Here are some of the most common queries from our customers.",
    "faq.q1": "How does airport parking work when I book in advance?",
    "faq.a1":
      "Booking your airport parking online is quick and simple. Choose your dates, select your preferred service, and secure your space instantly. Once booked, you'll receive confirmation details with clear arrival instructions for a smooth experience on the day.",
    "faq.q2": "Is my parking space guaranteed?",
    "faq.a2": "Yes. When you pre-book with us, your space is guaranteed. You won't need to worry about availability when you arrive, even during busy travel periods.",
    "faq.q3": "Is the car park secure?",
    "faq.a3": "Absolutely. Our airport parking facilities are regularly patrolled and monitored. We work with trusted operators to ensure your vehicle remains safe while you travel.",
    "faq.q4": "Do you offer a shuttle service to the terminal?",
    "faq.a4": "Yes, we provide a free 24/7 shuttle bus service for quick and convenient transfers to and from the terminal, ensuring easy access to your flight.",
    "faq.q5": "What is meet and greet parking?",
    "faq.a5": "Meet and greet parking allows you to drive directly to the terminal, where a professional driver collects your vehicle and parks it securely for you. When you return, your car is brought back to the agreed collection point.",
    "faq.q6": "How early should I arrive at the car park?",
    "faq.a6": "We recommend arriving at least 20–30 minutes before you need to be at the terminal. This allows time for parking and transfer without rushing.",
    "faq.q7": "Can I amend or cancel my booking?",
    "faq.a7": "Yes, we offer flexible booking options. Please check your booking confirmation for details on amendments or cancellations.",
    "faq.q8": "Why should I book airport parking in advance?",
    "faq.a8": "Pre-booking helps you secure the best available price, guarantees your space, and avoids the stress of finding parking on the day — especially during peak travel seasons.",
    "faq.still": "Still have a question?",
    "faq.agents": "Our Agents Will Be With You In Seconds",
    "faq.call": "Call Us Now",
    "faq.more": "More FAQs",

    // FAQs Page
    "faqsPage.title": "Airport Parking FAQs – Leeds Bradford Airport",
    "faqsPage.subtitle": "Answers to all your questions about off-site parking at Leeds Bradford Airport.",
    "faqsPage.metaTitle": "Airport Parking FAQs – Leeds Bradford Airport",
    "faqsPage.metaDesc": "Find answers to common questions about our off-site parking at Leeds Bradford Airport, including Park & Ride and Meet & Greet services.",

    // FAQs Page - Booking & Reservations
    "faqsPage.booking.title": "Booking & Reservations",
    "faqsPage.booking.q1": "How do I book a parking space?",
    "faqsPage.booking.a1": "You can easily book online through our website by selecting your dates, service type, and vehicle details.",
    "faqsPage.booking.q2": "Can I cancel or change my booking?",
    "faqsPage.booking.a2": "Yes, bookings can be modified or cancelled according to our terms and conditions. Check your booking confirmation for details.",
    "faqsPage.booking.q3": "Are there any discounts for early bookings?",
    "faqsPage.booking.a3": "Yes, booking in advance often provides cheaper rates and guarantees availability.",

    // FAQs Page - Parking Services
    "faqsPage.services.title": "Parking Services",
    "faqsPage.services.q1": "What is off-site parking at Leeds Bradford Airport?",
    "faqsPage.services.a1": "Off-site parking is a secure lot near the airport with transfers to the terminal via shuttle or Meet & Greet.",
    "faqsPage.services.q2": "What is Park & Ride service?",
    "faqsPage.services.a2": "Park & Ride allows you to park in our lot and take a shuttle bus to the terminal quickly and safely.",
    "faqsPage.services.q3": "What is Meet & Greet service?",
    "faqsPage.services.a3": "Meet & Greet lets you drive directly to the terminal where our staff will park your car for you, saving time and hassle.",
    "faqsPage.services.q4": "Is off-site parking cheaper than on-airport parking?",
    "faqsPage.services.a4": "Yes, off-site parking is generally more affordable than on-airport options while offering the same level of security and convenience.",

    // FAQs Page - Security & Safety
    "faqsPage.security.title": "Security & Safety",
    "faqsPage.security.q1": "Is your parking secure?",
    "faqsPage.security.a1": "Yes, all our off-site parking lots are monitored 24/7 with CCTV and on-site security for complete peace of mind.",
    "faqsPage.security.q2": "Are there any size restrictions for vehicles?",
    "faqsPage.security.a2": "We accommodate most standard vehicles. For larger cars or vans, please check our booking page for restrictions.",
    "faqsPage.security.q3": "Do you offer assistance for disabled passengers?",
    "faqsPage.security.a3": "Yes, our services are accessible, and staff can assist passengers with special requirements. Please mention this when booking.",

    // FAQs Page - Shuttle & Transfers
    "faqsPage.shuttle.title": "Shuttle & Transfers",
    "faqsPage.shuttle.q1": "How long does the shuttle take for Park & Ride?",
    "faqsPage.shuttle.a1": "Shuttle transfers are frequent and usually take 5–10 minutes to reach Leeds Bradford Airport.",
    "faqsPage.shuttle.q2": "Do I need to book the shuttle separately?",
    "faqsPage.shuttle.a2": "No, shuttle transfers are included in your Park & Ride booking.",

    // Footer
    "footer.desc": "provides reliable, affordable, and convenient parking solutions, trusted by thousands every year.",
    "footer.useful": "Useful Links",
    "footer.contact": "Contact",
    "footer.rights": "All rights reserved.",
    "footer.whyChoose": "Why Choose Us",
    "footer.services": "Services",
    "footer.about": "About",
    "footer.faqs": "FAQs",
    "footer.testimonials": "Testimonials",
    "footer.terms": "Terms & Conditions",
    "footer.privacy": "Privacy Policy",
    "footer.contactUs": "Contact Us",

    // Select Parking Page
    "selectParking.title": "Select Your Airport Parking Dates & Options",
    "selectParking.metaTitle": "Leeds Airport Parking | Select Dates & Book Online",
    "selectParking.metaDesc": "Book Leeds Bradford Airport parking easily. Select dates, check available options, and enjoy a smooth, stress-free start to your journey.",
    "selectParking.heroDesc": "Book your Leeds Airport parking quickly and stress-free",
    "selectParking.loading": "Loading parking options...",
    "selectParking.noOptions": "No parking options available for the selected dates.",
    "selectParking.all": "All",
    "selectParking.meetGreet": "Meet & Greet",
    "selectParking.parkRide": "Park & Ride",
    "selectParking.sortLowHigh": "Price: Low to High",
    "selectParking.sortHighLow": "Price: High to Low",
    "selectParking.bookNow": "Book Now",
    "selectParking.moreInfo": "More Info",

    // Booking Profile Page
    "profile.yourDetails": "Your Details",
    "profile.carDetails": "Car Details",
    "profile.flightDetails": "Flight Details",
    "profile.firstName": "First Name",
    "profile.surname": "Surname",
    "profile.email": "Email",
    "profile.contactNumber": "Contact Number",
    "profile.carRegistration": "Car Registration",
    "profile.carManufacturer": "Car Manufacturer",
    "profile.carModel": "Car Model",
    "profile.carColour": "Car Colour",
    "profile.departureTerminal": "Departure Terminal",
    "profile.returnTerminal": "Return Terminal",
    "profile.departureFlightNumber": "Departure Flight Number",
    "profile.returnFlightNumber": "Return Flight Number",
    "profile.selectTerminal": "Select terminal",
    "profile.bookNow": "Book Now",
    "profile.bookingSummary": "Booking Summary",
    "profile.parkingType": "Parking Type",
    "profile.entry": "Entry",
    "profile.exit": "Exit",
    "profile.price": "Price",
    "profile.bookingFee": "Booking Fee",
    "profile.total": "Total",
    "profile.refNumber": "Reference Number",
    "profile.timeLeft": "Time Left",
    "profile.required": "is required",
    "profile.validEmail": "Valid email is required",
    "profile.fillPersonal": "Please fill in all required personal details",
    "profile.fillCar": "Please fill in all required car details",
    "profile.fillAll": "Please fill in all required personal and car details",

    // Booking Confirm Page
    "confirm.title": "Confirm Your Booking",
    "confirm.bookingDetails": "Booking Details",
    "confirm.customerDetails": "Customer Details",
    "confirm.vehicleDetails": "Vehicle Details",
    "confirm.payNow": "Pay Now",
    "confirm.securePayment": "Secure Payment",
    "confirm.paymentInfo": "You will be redirected to a secure payment page",
    "confirm.readyToPay": "Ready to Pay",
    "confirm.clickToPay": "Click below to proceed to the secure payment page",
    "confirm.proceedToPayment": "Proceed to Payment",
    "confirm.redirecting": "Redirecting...",
    "confirm.redirectingToPayment": "Redirecting to Payment...",
    "confirm.verifyingPayment": "Verifying Payment...",
    "confirm.pleaseWait": "Please wait while we verify your payment",
    "confirm.preparingPayment": "Preparing secure payment...",
    "confirm.preparing": "Preparing...",
    "confirm.continueToThankYou": "Continue to Thank You",
    "confirm.transactionSuccessful": "Transaction Successful!",
    "confirm.redirectingThankYou": "Redirecting to Thank You page...",
    "confirm.bookingSummary": "Booking Summary",
    "confirm.bookingRef": "Booking Ref",
    "confirm.passenger": "Passenger",
    "confirm.email": "Email",
    "confirm.phone": "Phone",
    "confirm.car": "Car",
    "confirm.departureFlight": "Departure Flight",
    "confirm.returnFlight": "Return Flight",
    "confirm.bookingFee": "Booking Fee",
    "confirm.paymentDetails": "Payment Details",
    "confirm.paymentMethods": "Payment Methods",
    "confirm.mayTake": "This may take up to 30 seconds...",
    "confirm.processing": "Processing...",
    "confirm.pay": "Pay",
    "confirm.paymentCompletedQ": "Payment completed?",
    "confirm.paymentCompletedDesc": "If you've finished payment, continue to complete your booking.",
    "confirm.sendConfirmationTo": "We'll send confirmation to {email}.",
    "confirm.yourEmail": "your email",
    "confirm.secureBadge": "All payments are encrypted & 100% secure",

    // About Us Page
    "aboutPage.title": "About Go Airport Parking Leeds Bradford",
    "aboutPage.metaTitle": "About Us | Leeds Bradford Airport Off-Site Parking",
    "aboutPage.metaDesc": "Learn more about our secure off-site parking near Leeds Bradford Airport, offering reliable Park & Ride and Meet & Greet services at affordable prices.",
    "aboutPage.subtitle": "Secure and affordable off-site parking at Leeds Bradford Airport, offering reliable Park & Ride and Meet & Greet services.",
    "aboutPage.parking": "",
    "aboutPage.missionTitle": "Leeds Bradford Airport Parking",
    "aboutPage.missionText":
      "When booking Leeds Airport parking, it's important to choose the right service for your needs and allow plenty of time before your flight. Planning ahead helps you avoid unnecessary stress and ensures your journey gets off to a smooth start.\n\nLeeds Bradford Airport is a busy travel hub, and traffic around the airport can become congested at peak times. Arriving early and pre-booking your parking at Leeds Bradford Airport can make all the difference, especially during holidays and weekend departures.\n\nOur Meet and Greet service at Leeds Airport is designed to deliver convenience, reliability, and efficiency. We focus on three core principles: secure parking, professional service, and a seamless customer experience — giving you confidence from the moment you arrive.",
    "aboutPage.journeyTitle": "Our Journey",
    "aboutPage.trustTitle": "Why Trust Us",
    "aboutPage.coreValues": "Core Values",
    "aboutPage.customerService": "Customer Service",
    "aboutPage.cost": "Cost",
    "aboutPage.convenience": "Convenience",
    "aboutPage.trustedBrand": "Trusted Brand",
    "aboutPage.localExpertise": "Local Expertise",
    "aboutPage.customerFirst": "Customer First",
    "aboutPage.nationwideExperience": "Nationwide Experience",
    "aboutPage.tailoredService": "Tailored for Leeds Bradford Travelers",
    "aboutPage.transparentPricing": "Transparent Pricing",
    "aboutPage.qualityExtras": "Quality Services & Added Extras",
    "aboutPage.missionHistory": "Mission & History",
    "aboutPage.whyChoose": "Why Choose Go Airport Parking at Leeds Airport?",
    "aboutPage.bookNow": "Book Now",
    "aboutPage.timeline.2010": "Founded with a mission to make airport parking simple & affordable.",
    "aboutPage.timeline.2011":
      "Started with individual websites dedicated to different airports, building trust locally.",
    "aboutPage.timeline.2015": "Expanded nationwide across major UK airports with individual platforms.",
    "aboutPage.timeline.2020": "Celebrated 1 million travelers served across the UK.",
    "aboutPage.timeline.2023":
      "Brought everything together under the {brand} umbrella — a comparison platform uniting all sites.",
    "aboutPage.timeline.2024": "Upgraded facilities & introduced premium valet + extras.",
    "aboutPage.timeline.2025": "Launched {airport} services with territorial expertise.",
    "aboutPage.missionHistory.p1":
      "Since our beginnings as part of {brand}, our mission has been clear: make parking at airports simple, secure, and stress-free. We believe that the journey begins not when you board the plane but from the moment you leave your home.",
    "aboutPage.missionHistory.p2":
      "Our {airport} site builds on that legacy. We've selected convenient locations, invested in quality facilities, and trained our staff to uphold high standards—all so that our customers get the best possible experience under our trusted brand {brand}.",

    // About Us (dynamic descriptions)
    "aboutPage.trust.trustedBrandDesc":
      "Part of {brand}, a nationwide leader with years of experience in secure airport parking.",
    "aboutPage.trust.localExpertiseDesc":
      "Our {airport} team ensures your journey starts and ends smoothly, right from the airport gates.",
    "aboutPage.trust.customerFirstDesc":
      "Committed to delivering excellent service, transparent pricing, and reliable extras for every traveler.",
    "aboutPage.why.nationwideExperienceDesc":
      "With years of experience providing trusted airport parking across the UK, we bring proven systems, reliable operations, and customer-focused service to Leeds Airport parking. Our established reputation means you can book with confidence.",
    "aboutPage.why.tailoredServiceDesc":
      "Our service is designed specifically for those flying from Leeds Bradford Airport. From local staff who understand peak travel times to efficient drop-off and collection procedures, we focus on making parking at Leeds Airport smooth and hassle-free.",
    "aboutPage.why.transparentPricingDesc":
      "We believe in clear, upfront pricing with no hidden fees. When you book your parking at Leeds Bradford Airport, the price you see is exactly what you pay — no surprises.",
    "aboutPage.why.qualityExtrasDesc":
      "Enhance your journey with optional extras like valet parking, lounge access, and professional car care services. Choose the features that add genuine value to your Leeds Bradford car parking experience.",

    // Contact Page
    "contact.title": "Leeds Bradford Airport Parking Booking Help – Contact Us",
    "contact.metaTitle": "Contact Go Airport Parking Leeds Bradford | Booking Support & Enquiries",
    "contact.metaDesc": "Contact Go Airport Parking Leeds Bradford for quick help with your booking and enquiries. Get fast, friendly support for a stress-free airport experience.",
    "contact.subtitle": "Need help with your Leeds Bradford Airport parking booking? Our friendly team is here to answer your questions.",
    "contact.name": "Your Name",
    "contact.email": "Your Email",
    "contact.subject": "Subject",
    "contact.message": "Your Message",
    "contact.send": "Send Message",
    "contact.sending": "Sending...",
    "contact.success": "Message sent successfully!",
    "contact.error": "Failed to send message. Please try again.",
    "contact.phone": "Phone",
    "contact.emailLabel": "Email",
    "contact.address": "Address",
    "contact.sendMessageTitle": "Send Us a Message",
    "contact.enquiryQuestion": "What is your enquiry concerning?",
    "contact.enquiryTypePlaceholder": "Enquiry Type",
    "contact.enquiry.booking": "Booking Enquiry",
    "contact.enquiry.general": "General Enquiry",
    "contact.enquiry.complaint": "Complaint",
    "contact.enquiry.feedback": "Feedback",
    "contact.enquiry.partnership": "Partnership",
    "contact.titlePlaceholder": "Title",
    "contact.title.mr": "Mr",
    "contact.title.mrs": "Mrs",
    "contact.title.ms": "Ms",
    "contact.title.dr": "Dr",
    "contact.firstName": "First Name",
    "contact.surname": "Surname",
    "contact.contactNumber": "Contact Number",
    "contact.validationRequired": "Please fill in all required fields.",
    "contact.successDesc": "We'll get back to you as soon as possible. A confirmation has been sent to your email.",

    // FAQs Page (legacy keys kept for compatibility)

    // Blogs Page
    "blogsPage.title": "Airport Parking Tips & Guides – Leeds Bradford Airport",
    "blogsPage.metaTitle": "Airport Parking Tips & Guides – Leeds Bradford Airport",
    "blogsPage.metaDesc": "Read expert tips, guides, and advice on off-site parking at Leeds Bradford Airport. Learn about Park & Ride and Meet & Greet services.",
    "blogsPage.subtitle": "Airport parking tips and guides for Leeds Bradford Airport – Park & Ride and Meet & Greet included.",
    "blogsPage.readMore": "Read More",
    "blogsPage.noPosts": "No blog posts available yet.",

    // Testimonials Page
    "testimonialsPage.title": "Customer Reviews",
    "testimonialsPage.subtitle": "See what our customers say about their experience with us",

    // International Page
    "international.title": "Airport Parking Network Across UK & Dublin Airports",
    "international.metaTitle": "Airport Parking Network | UK Airports & Dublin Airport",
    "international.metaDesc": "Explore our airport parking network across UK airports and Dublin Airport. Enjoy secure off-site Park & Ride and Meet & Greet services at affordable prices.",
    "international.subtitle": "Explore our airport parking network across UK airports and Dublin Airport. Enjoy secure off-site Park & Ride and Meet & Greet services at affordable prices.",
    "international.servicesTitle": "Our Parking Services",
    "international.servicesSubtitle": "Choose the service that best fits your travel needs",
    "international.service.meetGreet.title": "Meet & Greet",
    "international.service.meetGreet.desc":
      "Hand over your car at the terminal, and our professional team will park it securely for you while you travel stress-free.",
    "international.service.parkRide.title": "Park & Ride",
    "international.service.parkRide.desc":
      "Park at our secure lot and enjoy a free shuttle service directly to your terminal—convenient, fast, and hassle-free.",
    "international.service.valet.title": "Valet Parking",
    "international.service.valet.desc":
      "Experience full luxury valet service at the terminal. Hand over your keys and enjoy a seamless, stress-free journey.",

    // Booking Stepper
    "stepper.selectDates": "Select Dates",
    "stepper.chooseParking": "Choose Parking",
    "stepper.yourDetails": "Your Details",
    "stepper.payment": "Payment",

    // Dates Summary
    "datesSummary.title": "Your Trip",
    "datesSummary.entry": "Entry",
    "datesSummary.exit": "Exit",
    "datesSummary.duration": "Duration",
    "datesSummary.days": "days",
    "datesSummary.day": "day",
    "datesSummary.promoCode": "Promo Code",
    "datesSummary.editDates": "Update",

    // Booking Cancelled
    "cancel.title": "Payment not completed",
    "cancel.cancelledDesc": "Your payment was not completed. You can retry the payment or go back to home.",
    "cancel.failedDesc": "Your payment was declined or failed. You can retry the payment or go back to home.",
    "cancel.reference": "Reference:",
    "cancel.retry": "Retry Payment",
    "cancel.home": "Go to Home",

    // Common
    "common.loading": "Loading...",
    "common.error": "An error occurred",
    "common.retry": "Retry",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.save": "Save",
    "common.close": "Close",
    "common.back": "Back",
    "common.next": "Next",
    "common.submit": "Submit",
  },
};

/**
 * Translates dynamic text from API — returns text as-is
 */
export const translateDynamicText = (text: string, _language: Language): string => {
  return text;
};

/**
 * Get translation for a static key
 */
export const getTranslation = (key: string, _language: Language): string => {
  return translations.en[key] || key;
};
